/**
 * PlayerVisuals — character animation helpers.
 *
 * The squash-and-stretch curve is v1's, unchanged, because it is the single
 * cheapest thing in the whole renderer that makes a jump feel like a jump.
 *
 * ── The two v1 bugs this file exists to fix ────────────────────────────────
 *
 * `crownMesh` was a MODULE-LEVEL SINGLETON (v1 PlayerVisuals.js:119). With one
 * player that reads as working; with eight it means only one player can ever
 * wear the crown, and giving it to a second player silently steals it from the
 * first. `idleTime` (v1 :105) had the same shape — one accumulator shared by
 * every caller, so eight characters bobbed in lockstep off whichever one was
 * updated last.
 *
 * Both are now per-mesh, stored in `mesh.userData`. There is no module state in
 * this file at all, which is the property that makes it correct for N players.
 */

import * as THREE from 'three/webgpu';
import { createAccentMaterial } from './ToonMaterials.js';

const REST_SCALE = new THREE.Vector3(1, 1, 1);
/** Scratch. Safe to share because it never survives a single call. */
const _targetScale = new THREE.Vector3(1, 1, 1);

/**
 * Rising and falling read differently: stretch on the way up, squash on the way
 * down, and hold rest in the dead band between so walking does not wobble.
 *
 * Landing gets its own impulse. Without it the squash just lerps back to rest
 * as vy hits zero and the impact frame — the one that sells weight — is the
 * softest frame of the whole fall. The peak fall speed is LATCHED because both
 * callers smear a landing across several decelerating frames (snapshot
 * interpolation remotely, the predictor's ground resolve locally), so the frame
 * that reads grounded never sees the pre-impact velocity directly.
 */
export function updateSquashStretch(mesh, velocityY) {
  if (!mesh) return;
  const ud = mesh.userData;
  const now = performance.now();

  if (velocityY < -12) ud.fallPeakVy = Math.min(ud.fallPeakVy ?? 0, velocityY);
  if (velocityY >= -1 && (ud.fallPeakVy ?? 0) < -12) {
    // -45 is a hard fall; harder ones clamp rather than flatten to a pancake.
    ud.landSquash = Math.min(1, -ud.fallPeakVy / 45);
    ud.landDur = 140 + ud.landSquash * 90;
    ud.landUntil = now + ud.landDur;
    ud.fallPeakVy = 0;
  }

  if (now < (ud.landUntil ?? 0)) {
    // Snap in hard, ease out with the timer.
    const s = ud.landSquash * ((ud.landUntil - now) / ud.landDur);
    _targetScale.set(1 + s * 0.45, 1 - s * 0.5, 1 + s * 0.45);
    mesh.scale.lerp(_targetScale, 0.5);
    return;
  }

  if (velocityY > 5) {
    const t = Math.min(velocityY / 25, 0.25);
    _targetScale.set(1 - t * 0.5, 1 + t, 1 - t * 0.5);
  } else if (velocityY < -10) {
    const t = Math.min(Math.abs(velocityY) / 50, 0.2);
    _targetScale.set(1 + t * 0.5, 1 - t, 1 + t * 0.5);
  } else {
    _targetScale.copy(REST_SCALE);
  }

  mesh.scale.lerp(_targetScale, 0.15);
}

function buildCrown() {
  const crown = new THREE.Group();
  const mat = createAccentMaterial(0xffd700);

  const base = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.35, 0.15, 8), mat);
  crown.add(base);

  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const point = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.2, 4), mat);
    point.position.set(Math.cos(angle) * 0.25, 0.17, Math.sin(angle) * 0.25);
    crown.add(point);
  }

  crown.position.y = 1.25;
  crown.userData.noOutline = true;
  return crown;
}

/**
 * Marks the current threat — most kills, or the last two standing.
 *
 * Per-instance. Eight simultaneous callers each own their own crown.
 */
export function updateLeaderCrown(playerMesh, isLeader) {
  if (!playerMesh) return;
  const existing = playerMesh.userData.crownMesh ?? null;

  if (isLeader && !existing) {
    const crown = buildCrown();
    // Procedural bodies vary in head height per seed and say where a crown
    // sits; the fixed 1.25 stays as the fallback for anything that doesn't.
    crown.position.y = playerMesh.userData.crownY ?? crown.position.y;
    playerMesh.add(crown);
    playerMesh.userData.crownMesh = crown;
    return;
  }

  if (!isLeader && existing) {
    playerMesh.remove(existing);
    for (const child of existing.children) child.geometry.dispose();
    existing.children[0]?.material.dispose();
    playerMesh.userData.crownMesh = null;
  }
}

/**
 * Cast anticipation: a brief crouch during an ability's wind-up so the telegraph
 * on the ground has a matching pose on the body.
 */
export function applyCastAnticipation(mesh, windupMs) {
  if (!mesh || windupMs <= 0) return;
  mesh.userData.castUntil = performance.now() + windupMs;
}

export function updateCastPose(mesh, now) {
  if (!mesh) return;
  const until = mesh.userData.castUntil ?? 0;
  if (now >= until) return;
  // Squash slightly and lean into the shot. Blends with squash-stretch because
  // both write the same scale and the last writer this frame is this one.
  mesh.scale.y *= 0.94;
  mesh.scale.x *= 1.03;
  mesh.scale.z *= 1.03;
}
